import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import axios from 'axios';
import CodeEditor from './CodeEditor';
import EditorConsolePanel from './editor/EditorConsolePanel';
import WebPreviewPanel from './editor/WebPreviewPanel';
import { editorFileNameForLanguage } from './editor/EditorBreadcrumb';
import {
  PROGRAMIZ,
  programizRunButtonSx,
  programizSecondaryButtonSx,
} from './editor/programizTheme';
import { isWebPreviewLanguage } from './editor/htmlBoilerplate';
import { getLanguageLabel } from './editor/languageManager';
import { getAttachmentUrl } from './AttachmentPreviewPanel';
import AndroidOnlyDocumentNotice from './AndroidOnlyDocumentNotice';
import AttachmentPreviewPanel from './AttachmentPreviewPanel';
import { hasWebDocumentAccess } from '../../../utils/studentDevAccess';
import { createAutoSaveHandler, loadLocalDraft } from './editor/draftManager';
import {
  normalizeLanguage,
  normalizeStarterCode,
  resolveStarterCode,
  shouldApplyTemplate,
} from './editor/languageTemplates';
import { normalizeIdeExperience } from './editor/ideExperienceProfile';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getStudentAuthHeaders = () => {
  const token = localStorage.getItem('student_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getStudentPhone = () => {
  try {
    const raw = localStorage.getItem('student_data');
    const parsed = raw ? JSON.parse(raw) : null;
    return String(parsed?.phone || '').replace(/\D/g, '').slice(-10);
  } catch {
    return '';
  }
};

const parseAttachment = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const fileUrl = getAttachmentUrl(raw);
  if (!fileUrl) return null;
  return {
    fileUrl,
    fileName: raw.fileName || raw.file_name || 'attachment',
    mimeType: raw.mimeType || raw.mime_type || '',
  };
};

const readyStorageKey = (contentId) => `coding_challenge_ready_${contentId}`;

const isMarkedReady = (contentId) => {
  try {
    return sessionStorage.getItem(readyStorageKey(contentId)) === '1';
  } catch {
    return false;
  }
};

const markReady = (contentId) => {
  try {
    sessionStorage.setItem(readyStorageKey(contentId), '1');
  } catch {
    // ignore
  }
};

const parseCoding = (contentData) => {
  const raw = contentData?.codingData || contentData?.coding || contentData || {};
  return {
    instructions: raw.instructions || '',
    languages: raw.languages || ['python', 'c', 'cpp', 'java'],
    defaultLanguage: raw.defaultLanguage || 'python',
    starterCode: normalizeStarterCode(raw.starterCode || {}),
    totalMarks: Number(raw.totalMarks || 10),
    autoGradingEnabled: raw.autoGradingEnabled !== false,
    allowRun: raw.allowRun !== false,
    allowSubmit: raw.allowSubmit !== false,
    ideExperience: normalizeIdeExperience(raw.ideExperience || raw.ide_experience),
    sampleTestCases: Array.isArray(raw.sampleTestCases) ? raw.sampleTestCases : [],
    challengeAttachment: parseAttachment(
      raw.challengeAttachment || raw.challenge_attachment,
    ),
    keyAttachment: parseAttachment(raw.keyAttachment || raw.key_attachment),
  };
};

const parseTestResults = (submission) => {
  const raw = submission?.test_results || submission?.testResults;
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => ({
    index: index + 1,
    passed: Boolean(item?.passed),
    description: item?.description || `Test ${index + 1}`,
    input: item?.input,
    expectedOutput: item?.expectedOutput || item?.expected_output,
  }));
};

const focusedFieldSx = {
  '& .MuiInputLabel-root': { color: '#858585' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#007ACC' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3C3C3C' },
  '& .MuiInputBase-root': { bgcolor: '#1E1E1E', color: '#D4D4D4' },
};

const focusedOutlinedButtonSx = {
  color: '#D4D4D4',
  borderColor: '#3C3C3C',
  '&:hover': { borderColor: '#007ACC', bgcolor: 'rgba(0,122,204,0.08)' },
};

function ChallengePosterView({ coding, title, onReady }) {
  const attachment = coding.challengeAttachment;
  const allowWebDocuments = hasWebDocumentAccess(getStudentPhone());

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        bgcolor: '#F5F6F8',
        color: '#1A1A2E',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              Challenge Brief
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mt: 0.5,
                lineHeight: 1.2,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              {title || 'Coding Challenge'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Explore the challenge poster below. When you are ready, open the coding workspace to
              write and run your program.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${coding.totalMarks} marks`} color="primary" />
            <Chip
              label={coding.autoGradingEnabled ? 'Auto-grading ON' : 'Manual review'}
              color={coding.autoGradingEnabled ? 'success' : 'warning'}
            />
          </Stack>

          {coding.instructions ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Instructions
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>
                {coding.instructions}
              </Typography>
            </Paper>
          ) : null}

              {attachment ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Challenge Poster
              </Typography>
              <Box sx={{ mt: 1 }}>
                {allowWebDocuments ? (
                  <AttachmentPreviewPanel attachment={attachment} />
                ) : (
                  <AndroidOnlyDocumentNotice />
                )}
              </Box>
            </Paper>
          ) : null}

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2,
              bgcolor: '#EEF4FF',
              borderColor: 'primary.light',
              position: { xs: 'sticky', sm: 'static' },
              bottom: { xs: 8, sm: 'auto' },
              zIndex: { xs: 2, sm: 'auto' },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The coding workspace opens in full-screen mode with the editor, terminal, and sample
              tests on one screen — no course sidebar distractions.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<RocketLaunchRoundedIcon />}
              onClick={onReady}
              sx={{ px: 3 }}
            >
              Ready for Challenge
            </Button>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}

function TestResultsPanel({ results, awardedMarks, totalMarks, status, isMobile, light = false }) {
  if (!results.length && status !== 'submitted' && status !== 'reviewed') return null;

  const passedCount = results.filter((r) => r.passed).length;
  const panelBorder = light ? PROGRAMIZ.border : '#3C3C3C';
  const panelBg = light ? PROGRAMIZ.bodyBg : '#252526';
  const titleColor = light ? PROGRAMIZ.text : '#E8E8E8';
  const mutedColor = light ? PROGRAMIZ.muted : '#858585';
  const textColor = light ? PROGRAMIZ.text : '#D4D4D4';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        borderColor: panelBorder,
        bgcolor: panelBg,
        boxShadow: light ? 'none' : undefined,
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent="space-between"
        spacing={isMobile ? 0.5 : 0}
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" sx={{ color: titleColor, fontWeight: 700 }}>
          Submission Results
        </Typography>
        {awardedMarks != null && totalMarks != null ? (
          <Chip
            size="small"
            label={`Score: ${awardedMarks}/${totalMarks}`}
            color="success"
            sx={{ height: 24 }}
          />
        ) : null}
      </Stack>

      {results.length > 0 ? (
        <>
          <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 1 }}>
            {passedCount}/{results.length} tests passed
          </Typography>
          <Stack spacing={0.75}>
            {results.map((result) => (
              <Stack
                key={result.index}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: result.passed ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)',
                  border: '1px solid',
                  borderColor: result.passed
                    ? 'rgba(76,175,80,0.35)'
                    : 'rgba(244,67,54,0.35)',
                }}
              >
                {result.passed ? (
                  <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#81C784', mt: 0.15 }} />
                ) : (
                  <CancelRoundedIcon sx={{ fontSize: 18, color: '#E57373', mt: 0.15 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: textColor, wordBreak: 'break-word' }}
                  >
                    {result.description}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: result.passed ? '#81C784' : '#E57373',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {result.passed ? 'PASS' : 'FAIL'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: mutedColor }}>
          Submitted for review. Test breakdown will appear when grading completes.
        </Typography>
      )}
    </Paper>
  );
}

function KeyPosterDialog({ open, onClose, attachment, isMobile }) {
  if (!attachment) return null;
  const allowWebDocuments = hasWebDocumentAccess(getStudentPhone());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { bgcolor: '#1E1E1E', backgroundImage: 'none', color: '#E8E8E8' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderBottom: '1px solid #3C3C3C' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <KeyRoundedIcon sx={{ color: '#81C784' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Answer Key Poster
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#CCCCCC' }} aria-label="Close">
          <CloseRoundedIcon />
        </IconButton>
      </Stack>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" sx={{ color: '#858585', mb: 2 }}>
          Available after submission. Review the solution reference below.
        </Typography>
        {allowWebDocuments ? (
          <AttachmentPreviewPanel attachment={attachment} />
        ) : (
          <AndroidOnlyDocumentNotice />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CodingRenderer({
  contentId,
  contentData,
  title = '',
  focusedMode = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const coding = useMemo(() => parseCoding(contentData), [contentData]);
  const studentPhone = getStudentPhone();
  const editorMinRows = isMobile ? 10 : isTablet ? 12 : focusedMode ? 14 : 18;

  const [phase, setPhase] = useState(() => {
    if (!focusedMode) return 'workspace';
    if (!coding.challengeAttachment) return 'workspace';
    if (isMarkedReady(contentId)) return 'workspace';
    return 'poster';
  });
  const [language, setLanguage] = useState(normalizeLanguage(coding.defaultLanguage));
  const [code, setCode] = useState(resolveStarterCode(coding.defaultLanguage, coding.starterCode));
  const [stdin, setStdin] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [keyAttachment, setKeyAttachment] = useState(null);
  const [keyPosterOpen, setKeyPosterOpen] = useState(false);
  const [selectedSampleId, setSelectedSampleId] = useState(
    coding.sampleTestCases[0]?.id || '',
  );
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [runError, setRunError] = useState('');
  const autoSaveRef = useRef(null);

  const locked = submission?.status === 'reviewed' || submission?.status === 'submitted';
  const testResults = useMemo(() => parseTestResults(submission), [submission]);
  const canViewKeyPoster = locked && Boolean(keyAttachment?.fileUrl);
  const isPreviewLang = isWebPreviewLanguage(language);

  const resolveInitialPhase = useCallback(
    (row) => {
      const isLocked =
        row?.status === 'reviewed' || row?.status === 'submitted';
      if (!focusedMode) return 'workspace';
      if (isLocked) return 'workspace';
      if (!coding.challengeAttachment) return 'workspace';
      if (isMarkedReady(contentId)) return 'workspace';
      return 'poster';
    },
    [contentId, coding.challengeAttachment, focusedMode],
  );

  const loadSubmission = useCallback(async () => {
    if (!contentId || !studentPhone) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/lms/coding/${contentId}/submission`, {
        headers: getStudentAuthHeaders(),
        params: { studentPhone },
      });
      const row = response.data?.data;
      setSubmission(row);
      setKeyAttachment(parseAttachment(row?.keyAttachment || row?.key_attachment));
      setPhase(resolveInitialPhase(row));
      if (row?.source_code) {
        setCode(row.source_code);
      }
      if (row?.language) {
        setLanguage(row.language);
      }
      if (Array.isArray(row?.test_results) && row.test_results.length) {
        const summary = row.test_results
          .map((r, i) => `Test ${i + 1}: ${r.passed ? 'PASS' : 'FAIL'}`)
          .join('\n');
        setOutput(summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load your submission.');
      setPhase(resolveInitialPhase(null));
    }
  }, [contentId, studentPhone, resolveInitialPhase]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  useEffect(() => {
    const localDraft = loadLocalDraft(contentId, studentPhone);
    if (localDraft?.code && !submission?.source_code) {
      setCode(localDraft.code);
      if (localDraft.language) setLanguage(localDraft.language);
    }
  }, [contentId, studentPhone, submission?.source_code]);

  useEffect(() => {
    autoSaveRef.current = createAutoSaveHandler({
      contentId,
      studentPhone,
      getState: () => ({ language, code }),
    });
    autoSaveRef.current.start();
    return () => autoSaveRef.current?.stop();
  }, [contentId, studentPhone, language, code]);

  const handleReadyForChallenge = () => {
    markReady(contentId);
    setPhase('workspace');
  };

  const handleLanguageChange = (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguage(normalized);
    if (locked) return;
    if (shouldApplyTemplate(code, coding.starterCode)) {
      setCode(resolveStarterCode(normalized, coding.starterCode));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(
        `${API_BASE_URL}/lms/coding/${contentId}/save`,
        { language, code, studentPhone },
        { headers: getStudentAuthHeaders() },
      );
      setNotice('Draft saved.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async (testCaseId) => {
    setRunning(true);
    setError('');
    setRunError('');
    setNotice('');
    setOutput('');
    setConsoleOpen(true);
    try {
      if (isWebPreviewLanguage(language)) {
        // Live iframe preview — no server run.
        setNotice('Preview updated.');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/lms/coding/${contentId}/run`,
        {
          language,
          code,
          stdin: testCaseId ? undefined : stdin,
          testCaseId: testCaseId || undefined,
          studentPhone,
        },
        { headers: getStudentAuthHeaders() },
      );
      const data = response.data?.data || {};
      const lines = [
        data.stdout ? `stdout:\n${data.stdout}` : '',
        data.stderr ? `stderr:\n${data.stderr}` : '',
        data.compileOutput ? `compile:\n${data.compileOutput}` : '',
        data.passed === true ? '\n✓ Sample test passed' : '',
        data.passed === false ? '\n✗ Sample test failed' : '',
      ].filter(Boolean);
      setOutput(lines.join('\n\n'));
    } catch (err) {
      setRunError(err?.response?.data?.message || 'Run failed.');
    } finally {
      setRunning(false);
    }
  };

  const handleClearOutput = useCallback(() => {
    setOutput('');
    setRunError('');
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/lms/coding/${contentId}/submit`,
        { language, code, studentPhone },
        { headers: getStudentAuthHeaders() },
      );
      const row = response.data?.data;
      setSubmission(row);
      setKeyAttachment(parseAttachment(row?.keyAttachment || row?.key_attachment));
      setNotice(response.data?.message || 'Submitted successfully.');
      if (Array.isArray(row?.test_results)) {
        const summary = row.test_results
          .map((r, i) => `Test ${i + 1}: ${r.passed ? 'PASS' : 'FAIL'}`)
          .join('\n');
        setOutput(summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (focusedMode && phase === 'poster') {
    return (
      <ChallengePosterView
        coding={coding}
        title={title}
        onReady={handleReadyForChallenge}
      />
    );
  }

  if (focusedMode) {
    const fileName = editorFileNameForLanguage(language);

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: PROGRAMIZ.bodyBg,
          overflow: 'hidden',
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ flexShrink: 0, borderRadius: 0 }}>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="success" sx={{ flexShrink: 0, borderRadius: 0 }}>
            {notice}
          </Alert>
        ) : null}
        {submission?.remarks ? (
          <Alert severity="info" sx={{ flexShrink: 0, borderRadius: 0 }}>
            Remarks: {submission.remarks}
          </Alert>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
          }}
        >
          {/* Editor — left */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 280, md: 0 },
              display: 'flex',
              flexDirection: 'column',
              borderRight: { md: `1px solid ${PROGRAMIZ.border}` },
              borderBottom: { xs: `1px solid ${PROGRAMIZ.border}`, md: 'none' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: { xs: 1, sm: 1.5 },
                minHeight: 44,
                bgcolor: PROGRAMIZ.headerBg,
                borderBottom: `1px solid ${PROGRAMIZ.border}`,
                flexShrink: 0,
                overflowX: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.75,
                    borderBottom: `2px solid ${PROGRAMIZ.tabActive}`,
                    bgcolor: PROGRAMIZ.bodyBg,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: PROGRAMIZ.text, fontWeight: 500, fontSize: 14, maxWidth: 120 }}
                  >
                    {fileName}
                  </Typography>
                </Box>

                {coding.languages.length > 1 ? (
                  <Select
                    size="small"
                    value={language}
                    disabled={locked}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    variant="standard"
                    disableUnderline
                    sx={{
                      color: PROGRAMIZ.muted,
                      fontSize: 13,
                      minWidth: 72,
                      '& .MuiSelect-select': { py: 0.25, pr: 2.5 },
                      '& .MuiSvgIcon-root': { color: PROGRAMIZ.muted, fontSize: 18 },
                    }}
                  >
                    {coding.languages.map((lang) => (
                      <MenuItem key={lang} value={lang} sx={{ fontSize: 13 }}>
                        {getLanguageLabel(lang)}
                      </MenuItem>
                    ))}
                  </Select>
                ) : null}

                {coding.sampleTestCases.length > 0 ? (
                  <Select
                    size="small"
                    value={selectedSampleId}
                    disabled={locked}
                    onChange={(e) => setSelectedSampleId(e.target.value)}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    sx={{
                      color: PROGRAMIZ.muted,
                      fontSize: 13,
                      minWidth: 88,
                      maxWidth: 140,
                      '& .MuiSelect-select': { py: 0.25, pr: 2.5 },
                      '& .MuiSvgIcon-root': { color: PROGRAMIZ.muted, fontSize: 18 },
                    }}
                  >
                    {coding.sampleTestCases.map((tc, index) => (
                      <MenuItem key={tc.id || index} value={tc.id} sx={{ fontSize: 13 }}>
                        {tc.description || `Sample ${index + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                ) : null}
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                {coding.allowRun ? (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        isPreviewLang ? (
                          <VisibilityRoundedIcon sx={{ fontSize: '18px !important' }} />
                        ) : (
                          <PlayArrowRoundedIcon sx={{ fontSize: '18px !important' }} />
                        )
                      }
                      disabled={
                        locked ||
                        running ||
                        (!isPreviewLang && !studentPhone)
                      }
                      onClick={() => handleRun()}
                      sx={programizRunButtonSx}
                    >
                      {isPreviewLang
                        ? running
                          ? 'Previewing...'
                          : 'Preview'
                        : running
                          ? 'Running...'
                          : 'Run'}
                    </Button>
                    {!isPreviewLang && selectedSampleId ? (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={locked || running || !studentPhone}
                        onClick={() => handleRun(selectedSampleId)}
                        sx={programizSecondaryButtonSx}
                      >
                        Sample
                      </Button>
                    ) : null}
                  </>
                ) : null}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={locked || saving || !studentPhone}
                  onClick={handleSave}
                  sx={programizSecondaryButtonSx}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                {coding.allowSubmit ? (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={locked || submitting || !studentPhone}
                    onClick={handleSubmit}
                    sx={{
                      ...programizSecondaryButtonSx,
                      borderColor: PROGRAMIZ.tabActive,
                      color: PROGRAMIZ.tabActive,
                    }}
                  >
                    {submitting ? '...' : locked ? 'Submitted' : 'Submit'}
                  </Button>
                ) : null}
                {canViewKeyPoster ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setKeyPosterOpen(true)}
                    sx={{
                      ...programizSecondaryButtonSx,
                      borderColor: '#16A34A',
                      color: '#16A34A',
                    }}
                  >
                    Key
                  </Button>
                ) : null}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
              <CodeEditor
                language={language}
                value={code}
                onChange={setCode}
                readOnly={locked}
                contentId={contentId}
                fileName={fileName}
                showToolbar={false}
                showStatusBar={false}
                showBreadcrumb={false}
                themeId="vs-light"
                fillHeight
              />
            </Box>
          </Box>

          {/* Output / Preview — right */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 220, md: 0 },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isPreviewLang ? (
              <WebPreviewPanel
                language={language}
                code={code}
                fillHeight
                variant="programiz"
              />
            ) : (
              <EditorConsolePanel
                variant="programiz"
                fillHeight
                output={output}
                error={runError}
                running={running}
                open={consoleOpen}
                stdin={stdin}
                onStdinChange={setStdin}
                readOnly={locked}
                showStdin={coding.allowRun}
                stdinMaxRows={isMobile ? 1 : 2}
                onClear={handleClearOutput}
              />
            )}
          </Box>
        </Box>

        {locked ? (
          <Box
            sx={{
              flexShrink: 0,
              maxHeight: { xs: 180, md: 220 },
              overflow: 'auto',
              borderTop: `1px solid ${PROGRAMIZ.border}`,
              p: 1,
              bgcolor: PROGRAMIZ.headerBg,
            }}
          >
            <TestResultsPanel
              results={testResults}
              awardedMarks={submission?.awarded_marks}
              totalMarks={submission?.total_marks}
              status={submission?.status}
              isMobile={isMobile}
              light
            />
          </Box>
        ) : null}

        {!studentPhone ? (
          <Alert severity="warning" sx={{ flexShrink: 0, borderRadius: 0 }}>
            Student session is missing. Please login again.
          </Alert>
        ) : null}

        <KeyPosterDialog
          open={keyPosterOpen}
          onClose={() => setKeyPosterOpen(false)}
          attachment={keyAttachment}
          isMobile={isMobile}
        />
      </Box>
    );
  }

  const workspaceShellSx = focusedMode
    ? {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: isMobile ? 'auto' : 'hidden',
        WebkitOverflowScrolling: 'touch',
      }
    : {};

  const toolbarBg = focusedMode ? '#252526' : 'background.paper';
  const toolbarBorder = focusedMode ? '#3C3C3C' : 'divider';

  const actionButtons = (
    <>
      {coding.allowRun ? (
        <>
          <Button
            variant="outlined"
            size="small"
            fullWidth={isMobile}
            startIcon={
              isMobile
                ? null
                : isPreviewLang
                  ? <VisibilityRoundedIcon />
                  : <PlayArrowRoundedIcon />
            }
            disabled={locked || running || (!isPreviewLang && !studentPhone)}
            onClick={() => handleRun()}
            sx={focusedMode ? focusedOutlinedButtonSx : undefined}
          >
            {isMobile ? (
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                {isPreviewLang ? (
                  <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
                )}
                <span>
                  {isPreviewLang
                    ? running
                      ? '...'
                      : 'Preview'
                    : running
                      ? '...'
                      : 'Run'}
                </span>
              </Stack>
            ) : isPreviewLang ? (
              running ? 'Previewing...' : 'Preview'
            ) : (
              running ? 'Running...' : 'Run'
            )}
          </Button>
          {!isPreviewLang && selectedSampleId ? (
            <Button
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              disabled={locked || running || !studentPhone}
              onClick={() => handleRun(selectedSampleId)}
              sx={focusedMode ? focusedOutlinedButtonSx : undefined}
            >
              {isMobile ? 'Sample' : 'Run sample test'}
            </Button>
          ) : null}
        </>
      ) : null}
      <Button
        variant="outlined"
        size="small"
        fullWidth={isMobile}
        startIcon={isMobile ? null : <SaveRoundedIcon />}
        disabled={locked || saving || !studentPhone}
        onClick={handleSave}
        sx={focusedMode ? focusedOutlinedButtonSx : undefined}
      >
        {isMobile ? (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <SaveRoundedIcon sx={{ fontSize: 18 }} />
            <span>{saving ? '...' : 'Save'}</span>
          </Stack>
        ) : (
          saving ? 'Saving...' : 'Save draft'
        )}
      </Button>
      {coding.allowSubmit ? (
        <Button
          variant="contained"
          size="small"
          fullWidth={isMobile}
          startIcon={isMobile ? null : <SendRoundedIcon />}
          disabled={locked || submitting || !studentPhone}
          onClick={handleSubmit}
          sx={
            focusedMode
              ? { bgcolor: '#007ACC', '&:hover': { bgcolor: '#005a9e' } }
              : undefined
          }
        >
          {isMobile ? (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              <SendRoundedIcon sx={{ fontSize: 18 }} />
              <span>{submitting ? '...' : locked ? 'Done' : 'Submit'}</span>
            </Stack>
          ) : (
            submitting ? 'Submitting...' : locked ? 'Submitted' : 'Submit'
          )}
        </Button>
      ) : null}
      {canViewKeyPoster ? (
        <Button
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          startIcon={isMobile ? null : <KeyRoundedIcon />}
          onClick={() => setKeyPosterOpen(true)}
          sx={{
            color: '#81C784',
            borderColor: 'rgba(129,199,132,0.5)',
            '&:hover': { borderColor: '#81C784', bgcolor: 'rgba(129,199,132,0.08)' },
            gridColumn: isMobile ? '1 / -1' : 'auto',
          }}
        >
          {isMobile ? 'Key Poster' : 'View Key Poster'}
        </Button>
      ) : null}
    </>
  );

  const sampleTestsPanel = coding.sampleTestCases.length > 0 ? (
    isMobile ? (
      <Accordion
        defaultExpanded={false}
        sx={{
          flexShrink: 0,
          bgcolor: '#252526',
          color: '#E8E8E8',
          border: '1px solid #3C3C3C',
          borderRadius: '4px !important',
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#858585' }} />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScienceRoundedIcon sx={{ fontSize: 18, color: '#007ACC' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Sample tests ({coding.sampleTestCases.length})
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1}>
            {coding.sampleTestCases.map((tc, index) => (
              <Box
                key={tc.id || index}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid #3C3C3C',
                  bgcolor: '#1E1E1E',
                }}
              >
                <Typography variant="caption" sx={{ color: '#858585', fontWeight: 600 }}>
                  {tc.description || `Sample ${index + 1}`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: '#D4D4D4', wordBreak: 'break-word' }}>
                  Input: <code>{tc.input || '(empty)'}</code>
                </Typography>
                <Typography variant="body2" sx={{ color: '#D4D4D4', wordBreak: 'break-word' }}>
                  Expected: <code>{tc.expectedOutput || '(empty)'}</code>
                </Typography>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    ) : (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 1,
          borderColor: toolbarBorder,
          bgcolor: focusedMode ? '#252526' : 'background.paper',
          flexShrink: 0,
          maxHeight: focusedMode ? { sm: 160, md: 180 } : 'none',
          overflow: 'auto',
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, color: focusedMode ? '#E8E8E8' : 'text.primary' }}
        >
          Sample tests
        </Typography>
        <Stack spacing={1}>
          {coding.sampleTestCases.map((tc, index) => (
            <Box
              key={tc.id || index}
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: focusedMode ? '#3C3C3C' : 'divider',
                bgcolor: focusedMode ? '#1E1E1E' : 'transparent',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: focusedMode ? '#858585' : 'text.secondary', fontWeight: 600 }}
              >
                {tc.description || `Sample ${index + 1}`}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: focusedMode ? '#D4D4D4' : 'text.primary', wordBreak: 'break-word' }}>
                Input: <code>{tc.input || '(empty)'}</code>
              </Typography>
              <Typography variant="body2" sx={{ color: focusedMode ? '#D4D4D4' : 'text.primary', wordBreak: 'break-word' }}>
                Expected: <code>{tc.expectedOutput || '(empty)'}</code>
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    )
  ) : null;

  return (
    <Box sx={workspaceShellSx}>
      <Box
        sx={{
          flex: focusedMode ? 1 : 'none',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: focusedMode && !isMobile ? 'hidden' : 'visible',
          px: focusedMode ? { xs: 0.75, sm: 1.5 } : 0,
          py: focusedMode ? { xs: 0.75, sm: 1 } : 0,
          pb: focusedMode && isMobile ? 2 : focusedMode ? 1 : 0,
        }}
      >
        <Stack spacing={focusedMode ? { xs: 0.75, sm: 1 } : 2} sx={{ flex: 1, minHeight: 0 }}>
          {!focusedMode && coding.instructions ? (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {coding.instructions}
            </Typography>
          ) : null}

          {focusedMode && coding.instructions && !coding.challengeAttachment ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 1,
                borderColor: toolbarBorder,
                bgcolor: '#2D2D30',
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#D4D4D4', whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: 96, overflow: 'auto' }}
              >
                {coding.instructions}
              </Typography>
            </Paper>
          ) : null}

          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ flexShrink: 0, gap: 0.75 }}
          >
            <Chip
              label={`${coding.totalMarks} marks`}
              size="small"
              sx={focusedMode ? { bgcolor: '#3C3C3C', color: '#D4D4D4', height: 26 } : undefined}
            />
            <Chip
              label={isMobile
                ? (coding.autoGradingEnabled ? 'Auto-grade' : 'Manual')
                : (coding.autoGradingEnabled ? 'Auto-grading ON' : 'Manual review')}
              size="small"
              color={coding.autoGradingEnabled ? 'success' : 'warning'}
              sx={{ height: 26 }}
            />
            {submission?.status ? (
              <Chip
                label={isMobile ? submission.status : `Status: ${submission.status}`}
                size="small"
                color="primary"
                sx={{ height: 26 }}
              />
            ) : null}
            {submission?.status === 'reviewed' ? (
              <Chip
                label={`${submission.awarded_marks}/${submission.total_marks}`}
                size="small"
                color="success"
                sx={{ height: 26 }}
              />
            ) : null}
            {canViewKeyPoster && !isMobile ? (
              <Chip
                icon={<KeyRoundedIcon sx={{ fontSize: '16px !important' }} />}
                label="Answer key"
                size="small"
                color="success"
                variant="outlined"
                onClick={() => setKeyPosterOpen(true)}
                sx={{ cursor: 'pointer', height: 26 }}
              />
            ) : null}
          </Stack>

          {error ? <Alert severity="error" sx={{ flexShrink: 0 }}>{error}</Alert> : null}
          {notice ? <Alert severity="success" sx={{ flexShrink: 0 }}>{notice}</Alert> : null}
          {submission?.remarks ? (
            <Alert severity="info" sx={{ flexShrink: 0 }}>Remarks: {submission.remarks}</Alert>
          ) : null}

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 0.75, sm: 1 },
              borderRadius: 1,
              borderColor: toolbarBorder,
              bgcolor: toolbarBg,
              flexShrink: 0,
              position: focusedMode && isMobile ? 'sticky' : 'static',
              top: focusedMode && isMobile ? 0 : 'auto',
              zIndex: focusedMode && isMobile ? 3 : 'auto',
            }}
          >
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  label="Language"
                  size="small"
                  fullWidth
                  value={language}
                  disabled={locked}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  sx={{
                    minWidth: { sm: 140 },
                    flex: { sm: '0 0 140px' },
                    ...(focusedMode ? focusedFieldSx : {}),
                  }}
                >
                  {coding.languages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {getLanguageLabel(lang)}
                    </MenuItem>
                  ))}
                </TextField>
                {coding.sampleTestCases.length > 0 ? (
                  <TextField
                    select
                    label="Sample test"
                    size="small"
                    fullWidth
                    value={selectedSampleId}
                    disabled={locked}
                    onChange={(e) => setSelectedSampleId(e.target.value)}
                    sx={{
                      flex: 1,
                      ...(focusedMode ? focusedFieldSx : {}),
                    }}
                  >
                    {coding.sampleTestCases.map((tc, index) => (
                      <MenuItem key={tc.id || index} value={tc.id}>
                        {tc.description || `Sample ${index + 1}`}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? 'repeat(2, minmax(0, 1fr))'
                    : { sm: 'repeat(auto-fit, minmax(112px, auto))' },
                  gap: 0.75,
                  width: '100%',
                }}
              >
                {actionButtons}
              </Box>
            </Stack>
          </Paper>

          <Box
            sx={{
              flex: focusedMode && !isMobile ? 1 : 'none',
              minHeight: focusedMode ? { xs: 260, sm: 300, md: 320 } : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minHeight: { xs: 200, sm: 220 }, overflow: 'hidden' }}>
              <CodeEditor
                language={language}
                value={code}
                onChange={setCode}
                readOnly={locked}
                minRows={editorMinRows}
                contentId={contentId}
              />
            </Box>

            {isPreviewLang ? (
              <Box sx={{ minHeight: isMobile ? 160 : 220, flexShrink: 0 }}>
                <WebPreviewPanel language={language} code={code} variant="default" />
              </Box>
            ) : (
              <EditorConsolePanel
                output={output}
                error={runError}
                running={running}
                open={consoleOpen}
                stdin={stdin}
                onStdinChange={setStdin}
                readOnly={locked}
                showStdin={coding.allowRun}
                stdinMaxRows={isMobile ? 1 : focusedMode ? 2 : 1}
                outputMinHeight={isMobile ? 88 : 120}
                outputMaxHeight={isMobile ? 160 : isTablet ? 220 : 280}
              />
            )}
          </Box>

          {locked ? (
            <TestResultsPanel
              results={testResults}
              awardedMarks={submission?.awarded_marks}
              totalMarks={submission?.total_marks}
              status={submission?.status}
              isMobile={isMobile}
            />
          ) : null}

          {sampleTestsPanel}

          {!studentPhone ? (
            <Alert severity="warning" sx={{ flexShrink: 0 }}>
              Student session is missing. Please login again.
            </Alert>
          ) : null}
        </Stack>
      </Box>

      <KeyPosterDialog
        open={keyPosterOpen}
        onClose={() => setKeyPosterOpen(false)}
        attachment={keyAttachment}
        isMobile={isMobile}
      />
    </Box>
  );
}
